import { supabase } from "@/integrations/supabase/client";

export interface UserKeywordResult {
  id?: string;
  userId: string;
  keywordId: string;
  certificationId: string;
  chapterId: string;
  correctCount: number;
  incorrectCount: number;
  updatedAt?: string;
}

type Listener = () => void;
let _items: UserKeywordResult[] = [];
let _loaded = false;
const listeners = new Set<Listener>();
const notify = () => listeners.forEach((fn) => fn());

export const userKeywordResultsStore = {
  get: () => _items,
  subscribe: (fn: Listener) => {
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  },
  isLoaded: () => _loaded,

  async loadForUser(userId: string) {
    if (!userId) return;
    const { data, error } = await supabase
      .from("user_keyword_results" as any)
      .select("*")
      .eq("user_id", userId);
    if (error) {
      console.error("Failed to load keyword results:", error);
      return;
    }
    _items = (data || []).map((r: any) => ({
      id: r.id,
      userId: r.user_id,
      keywordId: r.keyword_id,
      certificationId: r.certification_id,
      chapterId: r.chapter_id,
      correctCount: r.correct_count,
      incorrectCount: r.incorrect_count,
      updatedAt: r.updated_at,
    }));
    _loaded = true;
    notify();
  },

  /** Record latest results for a batch of keywords (replaces previous counts per keyword) */
  async recordBatch(
    userId: string,
    certificationId: string,
    chapterId: string,
    results: { keywordId: string; correct: boolean }[]
  ) {
    for (const r of results) {
      const newCorrect = r.correct ? 1 : 0;
      const newIncorrect = r.correct ? 0 : 1;

      const { error } = await supabase
        .from("user_keyword_results" as any)
        .upsert({
          user_id: userId,
          keyword_id: r.keywordId,
          certification_id: certificationId,
          chapter_id: chapterId,
          correct_count: newCorrect,
          incorrect_count: newIncorrect,
          updated_at: new Date().toISOString(),
        }, { onConflict: "user_id,keyword_id,certification_id" });

      if (error) {
        console.error("Failed to upsert keyword result:", error);
        continue;
      }

      const idx = _items.findIndex(
        (i) => i.userId === userId && i.keywordId === r.keywordId && i.certificationId === certificationId
      );
      const updated: UserKeywordResult = {
        userId, keywordId: r.keywordId, certificationId, chapterId,
        correctCount: newCorrect,
        incorrectCount: newIncorrect,
      };
      if (idx >= 0) {
        _items[idx] = updated;
      } else {
        _items.push(updated);
      }
    }
    _items = [..._items];
    notify();
  },

  reset() {
    _items = [];
    _loaded = false;
    notify();
  },
};
